import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { showErrorToast, showWelcomeToast } from '../utils/toast';
import { login, loginWith2FA, verifyEmail, reset } from '../store/slices/authSlice';
import WelcomeModal from '../components/WelcomeModal';
import TwoFactorVerification from '../components/TwoFactorVerification';
import { useFirstTimeUser } from '../hooks/useFirstTimeUser';
import { authAPI } from '../utils/api';
import tokenManager from '../utils/tokenManager';

// Validation schema
const schema = yup.object({
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get the return URL from location state
  const from = location.state?.from?.pathname || '/dashboard';

  const { user, isLoading, isError, isSuccess, message, requiresTwoFactor, requiresVerification, verificationEmail } =
    useSelector(state => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { isFirstTime, isLoading: isFirstTimeLoading } = useFirstTimeUser();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    // Start token auto-refresh
    tokenManager.startAutoRefresh();
  }, []);

  useEffect(() => {
    if (isError && !requiresTwoFactor && !requiresVerification) {
      showErrorToast(message);
    }

    if (requiresVerification) {
      setShowVerificationModal(true);
      setRegisteredEmail(verificationEmail || loginCredentials?.email || '');
    }

    if (requiresTwoFactor) {
      setShowVerificationModal(false);
      setShow2FA(true);
    }

    if (isSuccess && user) {
      showWelcomeToast(user.name);
      setShow2FA(false);

      // Check if user is first-time after login success
      if (!isFirstTimeLoading && isFirstTime) {
        setShowWelcomeModal(true);
      } else {
        // Artwork-Only users have no Dashboard access; send them to /artwork.
        const destination = user.role === 'view-only' ? '/artwork' : from;
        navigate(destination, { replace: true });
      }
    }

    dispatch(reset());
  }, [
    user,
    isError,
    isSuccess,
    message,
    requiresTwoFactor,
    requiresVerification,
    verificationEmail,
    navigate,
    dispatch,
    isFirstTime,
    isFirstTimeLoading,
    from,
    loginCredentials,
  ]);

  const onSubmit = data => {
    setLoginCredentials(data);
    dispatch(login(data));
  };

  const handle2FAVerification = twoFactorData => {
    if (loginCredentials) {
      dispatch(
        loginWith2FA({
          email: loginCredentials.email,
          password: loginCredentials.password,
          ...twoFactorData,
        })
      );
    }
  };

  const handle2FACancel = () => {
    setShow2FA(false);
    setLoginCredentials(null);
    dispatch(reset());
  };

  const handleVerifyCode = () => {
    if (!verificationCode || verificationCode.length !== 6) {
      showErrorToast('Please enter a valid 6-digit code');
      return;
    }

    if (!registeredEmail) {
      showErrorToast('Missing email for verification. Please try logging in again.');
      return;
    }

    dispatch(verifyEmail({ email: registeredEmail, code: verificationCode }));
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isResending || !registeredEmail) return;

    setIsResending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend verification code');
      }

      setResendCooldown(300);
    } catch (error) {
      showErrorToast(error.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
    navigate(from, { replace: true });
  };

  const handleWelcomeModalDismiss = async () => {
    try {
      await authAPI.dismissWelcome();
      setShowWelcomeModal(false);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Error dismissing welcome modal:', error);
      // Still close the modal and navigate even if the API call fails
      setShowWelcomeModal(false);
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-white shadow-lg overflow-hidden">
            <img src="/favicon.ico" alt="AdBooker Logo" className="h-12 w-12 object-contain" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/signup" className="font-medium text-primary-600 hover:text-primary-500">
              create a new account
            </Link>
          </p>
          <div className="mt-4 text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                placeholder="Enter your email"
              />{' '}
              {errors.email && <p className="error-message">{errors.email.message}</p>}{' '}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password')}
                  className={`form-input pr-10 ${errors.password ? 'form-input-error' : ''}`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {showPassword ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    )}{' '}
                  </svg>
                </button>
              </div>
              {errors.password && <p className="error-message">{errors.password.message}</p>}{' '}
            </div>
          </div>

          {/* Forgot password link */}
          <div className="flex items-center justify-end">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          {/* Submit button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : null}
              {isLoading ? 'Signing in...' : 'Sign in'}{' '}
            </button>
          </div>
        </form>
      </div>

      {/* 2FA Verification Modal */}
      {show2FA && (
        <TwoFactorVerification
          onVerify={handle2FAVerification}
          onCancel={handle2FACancel}
          loading={isLoading}
          showTrustDevice={true}
        />
      )}

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h3>
              <p className="text-sm text-gray-600 mb-6">
                We sent a 6-digit verification code to
                <br />
                <span className="font-medium text-gray-900">{registeredEmail}</span>
              </p>

              <div className="mb-6">
                <label
                  htmlFor="verificationCode"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Enter Verification Code
                </label>
                <input
                  id="verificationCode"
                  type="text"
                  maxLength="6"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="form-input text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  autoFocus
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      handleVerifyCode();
                    }
                  }}
                />
              </div>

              <button
                onClick={handleVerifyCode}
                disabled={isLoading || verificationCode.length !== 6}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </button>

              <button
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || isResending}
                className="text-sm text-primary-600 hover:text-primary-500 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {isResending
                  ? 'Resending...'
                  : resendCooldown > 0
                    ? `Resend verification code (${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')})`
                    : 'Resend verification code'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeModalClose}
        onDismiss={handleWelcomeModalDismiss}
      />
    </div>
  );
};

export default Login;
